import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import type {
  CreditCardPaymentRequest,
  CreditCardPaymentResponse,
  CreditConfigurationInfo,
  NonPciCreditCardPaymentRequest,
} from '@ld.consumer-portal/models';
import { CreditCardModalComponent, CreditCardPaymentSuccess } from '../credit-card-modal/credit-card-modal.component';

export interface CreditCardDialogData {
  loanId: string;
  borrowerId: string;
  amount: number;
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  creditConfig?: CreditConfigurationInfo;
  lookupZipFn: (zip: string) => Promise<{ city: string; state: string } | null>;
  submitPaymentFn: (
    loanId: string,
    borrowerId: string,
    request: CreditCardPaymentRequest,
  ) => Promise<CreditCardPaymentResponse>;
  submitNonPciPaymentFn?: (
    loanId: string,
    borrowerId: string,
    request: NonPciCreditCardPaymentRequest,
  ) => Promise<CreditCardPaymentResponse>;
}

export type CreditCardDialogResult =
  | { action: 'cancel' }
  | { action: 'success'; data: CreditCardPaymentSuccess };

@Component({
  selector: 'ld-credit-card-dialog',
  imports: [CreditCardModalComponent],
  template: `
    <ld-credit-card-modal
      [loanId]="data.loanId"
      [borrowerId]="data.borrowerId"
      [amount]="data.amount"
      [creditConfig]="data.creditConfig"
      [firstName]="data.firstName ?? ''"
      [lastName]="data.lastName ?? ''"
      [address]="data.address ?? ''"
      [city]="data.city ?? ''"
      [state]="data.state ?? ''"
      [zip]="data.zip ?? ''"
      [lookupZipFn]="data.lookupZipFn"
      [submitPaymentFn]="data.submitPaymentFn"
      [submitNonPciPaymentFn]="data.submitNonPciPaymentFn"
      [embedded]="true"
      (success)="onSuccess($event)"
      (cancel)="onCancel()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditCardDialogComponent {
  private readonly dialogRef = inject<DialogRef<CreditCardDialogResult>>(DialogRef);
  protected readonly data = inject<CreditCardDialogData>(DIALOG_DATA);

  protected onSuccess(event: CreditCardPaymentSuccess): void {
    this.dialogRef.close({ action: 'success', data: event });
  }

  protected onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }
}
